/*
 * Copyright (c) 2022 automata-network/tgb-donation authors and contributors.
 * This software may be modified and distributed under the terms of MIT license.
 * Refer to the LICENSE file for details.
 */
import {
  PropsWithChildren,
  KeyboardEvent as ReactKeyboardEvent,
  MouseEvent as ReactMouseEvent,
  forwardRef
} from 'react'
import { pxToVw } from 'src/functions/units'

import styled, { css } from 'styled-components'

import { Color, ThemeUnits } from '../../types/theme'

export type BaseButtonAttributes = {
  bgColor?: Color | 'transparent'
  borderRadius?: keyof ThemeUnits['borderRadius']
  outlined?: boolean
  size?: keyof ThemeUnits['buttonSize']
  strokeColor?: Color
  strokeWidth?: number
  textColor?: Color
  textSize?: number | string | 'auto'
}

export type Props = PropsWithChildren<
  BaseButtonAttributes & {
    as?: keyof JSX.IntrinsicElements
    className?: string
    disabled?: boolean
    type?: 'button' | 'submit' | 'reset'
    onClick?: (e: ReactMouseEvent<HTMLButtonElement, MouseEvent>) => void
    onKeyDown?: (e: ReactKeyboardEvent<HTMLButtonElement>) => void
  }
>

const BaseButton = forwardRef<HTMLButtonElement, Props>(
  (
    {
      // Button attributes
      bgColor,
      borderRadius = 'small',
      outlined,
      size = 'medium',
      strokeColor,
      strokeWidth,
      textColor,
      textSize = 'auto',
      // Element attributes
      as,
      children,
      className,
      disabled = false,
      type = 'button',
      // Event handler
      onClick,
      onKeyDown
    }: Props,
    ref
  ) => {
    return (
      <StyledBaseButton
        ref={ref}
        $bgColor={bgColor}
        $borderRadius={borderRadius}
        $outlined={outlined}
        $size={size}
        $strokeColor={strokeColor}
        $strokeWidth={strokeWidth}
        $textColor={textColor}
        $textSize={textSize}
        as={as}
        className={className}
        disabled={disabled}
        type={type}
        onClick={onClick}
        onKeyDown={onKeyDown}
      >
        {children}
      </StyledBaseButton>
    )
  }
)

export default BaseButton

const StyledBaseButton = styled.button<
  {
    [K in keyof BaseButtonAttributes as `$${K}`]: BaseButtonAttributes[K]
  } & {
    disabled: boolean
    onClick: Props['onClick']
  }
>`
  appearance: none;
  padding-inline: ${pxToVw(17)};
  cursor: pointer;

  &:disabled {
    cursor: not-allowed;
  }

  ${({
    theme,
    disabled,
    onClick,
    $bgColor,
    $borderRadius,
    $outlined,
    $size,
    $strokeColor,
    $strokeWidth,
    $textColor,
    $textSize
  }) => css`
    background-color: ${$bgColor && !$outlined ? $bgColor : 'transparent'};
    color: ${$textColor};
    border-radius: ${$borderRadius && pxToVw(theme.units.borderRadius[$borderRadius])};
    height: ${$size && pxToVw(theme.units.buttonSize[$size])};
    cursor: ${onClick === undefined && !disabled ? 'default' : undefined};
    border: ${$outlined
      ? `solid ${$strokeWidth ?? 1}px ${$strokeColor ?? theme.colors.primary}`
      : 'none'};
    font-size: ${typeof $textSize === 'number' && $textSize > 0
      ? pxToVw($textSize)
      : typeof $textSize === 'string' && $textSize !== 'auto'
      ? $textSize
      : $size === 'small'
      ? pxToVw(theme.units.fontSize.regular)
      : pxToVw(theme.units.fontSize.body3)};

    ${theme.mediaQuery.mobile`
      padding-inline: ${pxToVw(17, 720)};
      height: ${$size && pxToVw(theme.units.buttonSize[$size], 720)};
      border-radius: ${$borderRadius && pxToVw(theme.units.borderRadius[$borderRadius], 720)};
      font-size: ${typeof $textSize === 'number' && $textSize > 0
        ? pxToVw($textSize, 720)
        : typeof $textSize === 'string' && $textSize !== 'auto'
        ? $textSize
        : $size === 'small'
        ? pxToVw(theme.units.fontSize.regular, 720)
        : pxToVw(theme.units.fontSize.body3, 720)};
    `}
  `}
`
